import { redirect } from "react-router";

import {
    clubhouseActions,
    selectLeague,
    selectSelectedLeagueTeam,
    selectSelectedTeam,
} from "../features/clubhouse/clubhouseSlice";
import { globalActions, selectSetting } from "../features/global/globalSlice";
import {
    leagueManagementActions,
    selectLeagues,
} from "../features/leaguemgmt/leagueManagementSlice";
import { userActions } from "../features/user/userSlice";
import { getTeam } from "../lib/api";
import { canEditTeam, canViewTeam } from "../lib/utils";
import { ILeague, ISeason, ITeam } from "../types/api";

import { store } from "..";

interface ICurrentTeamsLeague {
    id: ILeague["id"];
    name: string;
    year: ILeague["year"];
    season: ISeason;
}

const setHeader = (
    currentLeague: ICurrentTeamsLeague | undefined,
    page: string,
    request: Request,
) => {
    const pageHeaders: {
        [key: string]: string | (() => string);
    } = {
        league: `${currentLeague?.name} Standings`,
        "league-info": `${currentLeague?.name} League Info`,
        schedule: () => {
            let header = `${currentLeague?.name} Schedule`;
            const subPage = request.url.match(/schedule\/([^\/]+)(\/|$)/);

            if (subPage) {
                switch (subPage[1]) {
                    case "create-game":
                        header = `Schedule ${currentLeague?.name} Game`;
                        break;
                    case "reschedule-game":
                        header = `Reschedule ${currentLeague?.name} Game`;
                        break;
                    case "game":
                        const gamePage =
                            request.url.match(/\/game\/\d+\/(.*)$/);

                        if (gamePage) {
                            switch (gamePage[1]) {
                                case "logs":
                                    header = "Game Logs";
                                    break;
                                case "summary":
                                    header = "Game Summary";
                                    break;
                            }
                        }
                        break;
                }
            }

            return header;
        },
        roster: () => {
            let playerAction: string = "";
            const isOnRosterPage = /\/roster\/?$/.test(request.url);

            if (!isOnRosterPage) {
                playerAction = /team\/\d+\/roster\/add\/?$/.test(request.url)
                    ? "Add"
                    : "Edit";
            }
            return isOnRosterPage ? "Roster" : `${playerAction} Player`;
        },
        coi: "COI",
        "policy-procedures": "Policies & Procedures",
        tryouts: "Tryout Info",
        "looking-for-players": "Looking for Players",
        resume: `${currentLeague?.name || ""} Resume`,
        ratings: "Team Ratings",
        "pitch-counts": `${currentLeague?.name} Pitch Counts`,
        placement: `Placement`,
    };

    return typeof pageHeaders[page] === "function"
        ? (pageHeaders[page] as () => string)()
        : pageHeaders[page];
};

export const loader = async ({
    params,
    request,
}: {
    params: any;
    request: Request;
}) => {
    try {
        const teamId = parseInt(params.teamId);
        const url = new URL(request.url);
        const match = url.pathname.match(
            /\/coachs-clubhouse\/team\/\d+\/([^/]+)/,
        );
        const pageName: string | null = match ? match[1] : null;
        let state = store.getState();

        let teams = state.clubhouse.teams;
        const umpires = state.global.umpires;
        const ageLevels = state.global.ageLevels;
        const users = state.user.users;
        const settings = state.global.settings;
        const leagues = state.leaguemanagement.leagues;
        const allowedViewPages = ["roster", "pitch-counts"];
        let selectedTeam = selectSelectedTeam(state);
        let selectedLeagueTeam = selectSelectedLeagueTeam(state);
        const promises = [];

        !teams.length &&
            promises.push(
                store.dispatch(
                    clubhouseActions.getTeams({
                        userId: state.auth.user?.id as number,
                    }),
                ),
            );

        !ageLevels.length &&
            promises.push(store.dispatch(globalActions.getAgeLevels()));

        !users.length && promises.push(store.dispatch(userActions.getUsers()));

        !umpires.length &&
            promises.push(store.dispatch(globalActions.getUmpires()));

        !settings.length &&
            promises.push(store.dispatch(globalActions.getSettings()));

        !leagues.length &&
            promises.push(store.dispatch(leagueManagementActions.getLeagues()));

        await Promise.all(promises);

        state = store.getState();

        const currentYear = parseInt(
            selectSetting(state, "majorybl_current_league_year")?.setting,
        );

        let currentTeam: ITeam;
        if (teamId === selectedTeam?.id) {
            currentTeam = selectedTeam;
        } else if (teamId === selectedLeagueTeam?.id) {
            currentTeam = selectedLeagueTeam;
        } else {
            const fetchedTeam = await getTeam(teamId);
            currentTeam = fetchedTeam.team;
        }

        const currentLeague = selectLeagues(state).find(
            l =>
                l.teams &&
                l.teams.some(t => t.id === teamId) &&
                l.year === currentYear,
        );

        const currentTeamsLeague = currentTeam?.leagues?.find(
            l => l.year === currentYear,
        );

        if (!currentLeague || currentLeague.id !== selectLeague(state)?.id) {
            console.log("League changed from what is in state");
            store.dispatch(
                clubhouseActions.setLeague(currentLeague || undefined),
            );
        }

        if (canEditTeam(currentTeam)) {
            console.log("Can Edit Team");

            store.dispatch(clubhouseActions.setSelectedTeam(currentTeam.id));
        } else if (canViewTeam()) {
            if (pageName && allowedViewPages.includes(pageName)) {
                console.log("Can View Team");
                store.dispatch(
                    clubhouseActions.setSelectedLeagueTeam(currentTeam.id),
                );
            } else {
                console.log("page not allowed");

                return redirect("/coachs-clubhouse");
            }
        } else {
            console.log("Can't Edit or View Team");
            return redirect("/coachs-clubhouse");
        }

        const currentAgeLevel = currentTeam.ageLevels.find(
            t => t.year === currentYear,
        );

        const teamAgeLevel = currentTeam.ageLevels.reduce((prev, current) => {
            return new Date(current.year, current.season.id) >
                new Date(prev.year, prev.season.id)
                ? current
                : prev;
        }, currentTeam.ageLevels[0]);

        const ageLevel = currentAgeLevel || teamAgeLevel;

        const header =
            currentTeam &&
            (pageName
                ? `${currentTeam.name} ${
                      ageLevel ? `(${ageLevel.ageLevel})` : ""
                  } / ${setHeader(currentTeamsLeague, pageName, request)}`
                : `${currentTeam.name} ${
                      ageLevel ? `(${ageLevel.ageLevel})` : ""
                  }${`${currentTeamsLeague ? ` / ${currentTeamsLeague.name}` : ""}`}`);

        return {
            teamId: params.teamId,
            header,
        };
    } catch (e: any) {
        return {
            teamId: params.teamId,
            header: "",
        };
    }
};
